import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, effect, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { MultiSelectModule } from 'primeng/multiselect';
import { Subject, takeUntil } from 'rxjs';
import { BaseChartDirective } from 'ng2-charts';
import { ChartData, ChartOptions } from 'chart.js';
import { PaymentStats } from 'src/app/api/types/finance';
import { DashboardDataService } from 'src/app/routed-modules/mission-control/services/dashboard-data.service';
import { FinanceService } from 'src/app/api/finance.service';
import { IconService } from 'src/app/ui/icon.service';

@Component({
  selector: 'app-payment-stats-graph',
  standalone: true,
  imports: [CommonModule, CardModule, MultiSelectModule, FormsModule, BaseChartDirective],
  templateUrl: './payment-stats-graph.component.html',
  styleUrl: './payment-stats-graph.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaymentStatsGraphComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject<void>();

  // Loading state
  isLoading = signal<boolean>(true);

  // Chart configuration
  chartData = signal<ChartData<'line', number[], string>>({
    labels: [],
    datasets: []
  });

  chartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context) {
            const dataset = context.dataset;
            const value = dataset.data[context.dataIndex];
            return `${dataset.label}: $${value?.toLocaleString() ?? '0'}`;
          }
        }
      },
      title: {
        display: false,
        text: 'Payments Due'
      }
    },
    hover: {
      mode: 'x'
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 1250000,
        ticks: {
          stepSize: 250000,
          callback: function(value) {
            return '$' + value.toLocaleString();
          }
        }
      },
      x: {
        beginAtZero: true
      }
    }
  };

  constructor(
    private dashboardDataService: DashboardDataService,
    private financeService: FinanceService,
    public icons: IconService
  ) {
    // Load payment graph data with the correct year (2026)
    this.loadPaymentGraphData();
  }

  ngOnInit() {
    // Initialize data loading
    this.dashboardDataService.initializeData();
  }

  private loadPaymentGraphData() {
    // Load payment graph data without year parameter to get rolling date range like ColdFusion
    // ColdFusion uses: chargeDueDate > DATE_SUB(NOW(), INTERVAL 4 MONTH) AND chargeDueDate < DATE_ADD(NOW(), INTERVAL 8 MONTH)
    this.financeService.getPaymentStatsGraphData()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(data => {
        if (data && data.length > 0) {
          this.loadGraphData(data);
        }
      });
  }

  private loadGraphData(paymentData: any[]) {
    this.isLoading.set(true);
    
    // Process the payment data and update chart
    this.processPaymentData(paymentData);
    
    this.isLoading.set(false);
    
    // Force chart update after a short delay to ensure DOM is ready
    setTimeout(() => {
      this.chartData.update(current => ({ ...current }));
    }, 100);
  }

  private processPaymentData(data: any[]) {
    // Based on ColdFusion structure, data should be monthly totals
    // Each item should have: paymentDueMonth, totalPaid, totalDue
    
    const monthlyData = new Map<string, { totalDue: number; totalPaid: number }>();
    
    data.forEach((item) => {
      if (item.paymentDueMonth && item.totalDue !== undefined && item.totalPaid !== undefined) {
        // Store by month string (e.g., "01-2024")
        monthlyData.set(item.paymentDueMonth, {
          totalDue: item.totalDue,
          totalPaid: item.totalPaid
        });
      }
    });
    
    
    // Update chart data
    this.updateChartDataWithRealData(monthlyData);
  }

  private updateChartDataWithRealData(monthlyData: Map<string, { totalDue: number; totalPaid: number }>) {
    // Check if we have any data
    if (monthlyData.size === 0) {
      return;
    }
    
    // Match ColdFusion: Sort months chronologically by actual date (ORDER BY chargeDueDate)
    const sortedMonths = Array.from(monthlyData.keys()).sort((a, b) => {
      // Parse "MM-YYYY" format and compare as actual dates
      const [monthA, yearA] = a.split('-').map(Number);
      const [monthB, yearB] = b.split('-').map(Number);
      
      if (yearA !== yearB) {
        return yearA - yearB; // Sort by year first
      }
      return monthA - monthB; // Then by month
    });
    
    // Limit to 12 months (matching ColdFusion behavior - shows 12 months of data)
    const limitedMonths = sortedMonths.slice(0, 12);
    
    // Create labels from the month data - Match ColdFusion format exactly
    const labels = limitedMonths.map(monthStr => {
      // Convert "MM-YYYY" to "MMM YY" format (e.g., "01-2024" -> "Jan 24")
      const [month, year] = monthStr.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleString('default', { month: 'short' }) + ' ' + year.slice(-2);
    });
    
    // Create datasets for Total Due and Total Paid
    const totalDueData = limitedMonths.map(monthStr => monthlyData.get(monthStr)?.totalDue || 0);
    const totalPaidData = limitedMonths.map(monthStr => monthlyData.get(monthStr)?.totalPaid || 0);
    
    
    const datasets = [
      {
        label: 'Total Due',
        data: totalDueData,
        backgroundColor: 'rgba(26, 179, 148, 0.1)',
        borderColor: 'rgba(26, 179, 148, 0.7)',
        pointBorderColor: '#fff',
        pointBackgroundColor: 'rgba(26, 179, 148, 0.7)',
        pointHoverBackgroundColor: 'rgba(26, 179, 148, 0.7)',
        pointHoverBorderColor: 'rgba(26, 179, 148, 0.7)',
        tension: 0.4,
        fill: true
      },
      {
        label: 'Total Paid',
        data: totalPaidData,
        backgroundColor: 'rgba(150, 150, 150, 0.1)',
        borderColor: 'rgba(150, 150, 150, 0.8)',
        pointBackgroundColor: 'rgba(150, 150, 150, 0.8)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: 'rgba(150, 150, 150, 0.8)',
        pointHoverBorderColor: 'rgba(150, 150, 150, 0.8)',
        tension: 0.4,
        fill: true
      }
    ];

    const chartData = {
      labels: labels,
      datasets: datasets
    };
    
    this.chartData.set(chartData);
    
    // Force a chart update after setting data
    setTimeout(() => {
      this.chartData.update(current => ({ ...current }));
    }, 50);
  }





  private updateChartData(): void {
    // This method is called when the effect triggers
    // The actual data processing is handled in processPaymentData
    // We just need to trigger a re-render with the current data
    const currentData = this.dashboardDataService.yearlyPayments();
    if (currentData) {
      this.processPaymentData(currentData);
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
} 